import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Product } from "../../types/productTypes";
import api from "../../config/Api";

// 1. Fetching Products
export const fetchSellerProducts = createAsyncThunk<
  Product[],
  { jwt?: string | null; sellerId?: string } | void,
  { rejectValue: string }
>(
  "sellerProduct/fetchSellerProducts",
  async (params, { rejectWithValue }) => {
    try {
      const token = params?.jwt || localStorage.getItem("jwt");
      const url = params?.sellerId ? `/sellers/${params.sellerId}/products` : `/sellers/products`;
      
      const response = await api.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch products");
    }
  }
);

// 2. Creating Products
export const createProduct = createAsyncThunk<
    Product,
    { request: any; jwt: string | null },
    { rejectValue: string }
>(
    "sellerProduct/createProduct",
    async ({ request, jwt }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/sellers/products`, request, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to create product");
        }
    }
);

// 3. Updating Product
export const updateProduct = createAsyncThunk<
    Product,
    { productId: number; product: any; jwt: string | null },
    { rejectValue: string }
>(
    "sellerProduct/updateProduct",
    async ({ productId, product, jwt }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/sellers/products/${productId}`, product, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to update product");
        }
    }
);

// 4. Deleting Product
export const deleteProduct = createAsyncThunk<
    number,
    { productId: number; jwt: string | null },
    { rejectValue: string }
>(
    "sellerProduct/deleteProduct",
    async ({ productId, jwt }, { rejectWithValue }) => {
        try {
            await api.delete(`/sellers/products/${productId}`, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                },
            });
            return productId;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to delete product");
        }
    }
);

interface SellerProductState {
    products: Product[];
    loading: boolean;
    error: string | null;
}

const initialState: SellerProductState = {
    products: [],
    loading: false,
    error: null,
};

const sellerProductSlice = createSlice({
    name: "sellerProduct",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch Products
            .addCase(fetchSellerProducts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSellerProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.products = action.payload;
            })
            .addCase(fetchSellerProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to fetch products";
            })

            // Create Product
            .addCase(createProduct.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createProduct.fulfilled, (state, action: PayloadAction<Product>) => {
                state.loading = false;
                state.products.push(action.payload);
            })
            .addCase(createProduct.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to create product";
            })

            // Update Product
            .addCase(updateProduct.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateProduct.fulfilled, (state, action: PayloadAction<Product>) => {
                state.loading = false;
                const index = state.products.findIndex((p) => p.id === action.payload.id);
                if (index !== -1) {
                    state.products[index] = action.payload;
                }
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to update product";
            })

            // Delete Product
            .addCase(deleteProduct.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteProduct.fulfilled, (state, action: PayloadAction<number>) => {
                state.loading = false;
                state.products = state.products.filter((product) => product.id !== action.payload);
            })
            .addCase(deleteProduct.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to delete product";
            });
    },
});

export default sellerProductSlice.reducer;